import React, { FC } from 'react';
import { ComponentConsumerProps, ComponentRendering, Placeholder, Text, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import useMediaQuery from '../../hooks/useMediaQuery';
import NewsCard from './components/newsCard';
import { TNewsProps } from './types/news.types';
import { NewsModel } from './models/news.model';
import breakpoints from '../../constants/breakpoints.constants';
import { TCarouselSettings } from '../@shared/customCarousel/types/customCarousel.types';
import CustomExternalAnchor from '../@shared/customExternalAnchor';
import Icon from '../@shared/icon';
import { FieldEditFrameComponent } from '../@shared/editFrame';
import StyledLink from '../@shared/styledLink';
import CustomCarousel from '../@shared/customCarousel';
import fields from '../../constants/fields.const';
import { iconSize } from '../../constants/icon.constants';
import { findCtaType } from '../../utils/popUpCtaType.utils';
import AnalyticsVisibleEvent from '../@shared/analyticsVisibleEvent';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import { targetLinkdsk } from '../../utils/targetDskLink';
import './styles.scss';

const News: FC<ComponentConsumerProps & TNewsProps> = (props: TNewsProps) => {
  const model = new NewsModel(props);
  const isDesktop = useMediaQuery(`(min-width: ${breakpoints['tbl-p']}px)`);
  const displayCards = 3.4;
  const slidesForLessCards = model.cards.length < 4 ? model.cards.length - 0.7 : displayCards;
  const slidesToShow = model.cards.length === 1 ? 1 : slidesForLessCards;
  const generateCards = model.cards.map((item) => (
    <AnalyticsVisibleEvent
      key={item.id}
      event={'home_latest_updates_visible'}
      title={item?.fields?.title?.value}
      isEditorial={true}
      className="case-news-card__wrapper"
    >
      <NewsCard {...item} cardHeadingTitleTag={model.cardHeadingTitleTag} />
    </AnalyticsVisibleEvent>
  ));
  const isEEActive = props?.sitecoreContext?.pageEditing;
  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }
  const socialLinks = model?.socialLinks?.map((item) => {
    const canShowLinkInNormalMode =  !!(item?.fields?.link?.value?.href && item?.fields?.icon?.fields?.iconUrl?.value);
    return isEEActive ? (
      <div>
        <CustomExternalAnchor field={item.fields?.link} />
        <FieldEditFrameComponent itemId={item.id} fields={[fields.icon]} sitecoreContext={props?.sitecoreContext}>
          <Icon iconField={item.fields?.icon} color={'primary'} size={iconSize.dsk} />
        </FieldEditFrameComponent>
      </div>
    ) : canShowLinkInNormalMode && (
      <CustomExternalAnchor field={item?.fields?.link} key={item?.id} className={'case-news__social-icon-link'}>
        <Icon iconField={item?.fields?.icon} color={'primary'} size={iconSize.dsk} />
      </CustomExternalAnchor>
    );
  });

  const sliderSettings: TCarouselSettings = {
    slidesToShow,
    responsive: [
      { breakpoint: breakpoints['mbl'], settings: { slidesToShow: 1.1 } },
      { breakpoint: breakpoints['tbl-p'], settings: { slidesToShow: 2 } },
      { breakpoint: breakpoints['tbl-l'], settings: { slidesToShow: 2 } },
      { breakpoint: breakpoints['dsk'], settings: { slidesToShow: 2 } },
    ],
    infinite: false,
  };

  const popUpCtaType = findCtaType(props.rendering);
  const secondaryCTAClassName = popUpCtaType !== 'primary-cta' ? 'case-news__popup--secondary' : '';

  return (
    <>
      <div className="case-news">
        <div className="case-news__top">
          <div className="case-news__social">
            {model.socialText && <Text tag="span" className="case-news__social-text" field={model.socialText} />}
            <div className={`${!isEEActive ? 'case-news__social-icons' : ''}`}>{socialLinks}</div>
          </div>
          <div className="case-news__text">
            {model.title && <Text tag={model.headingTag} field={model.title} className="case-news__title" />}
            {model.subtitle && <Text tag="p" field={model.subtitle} className="case-news__subtitle" />}
            {!!(isEEActive || (model.AllNews?.value?.href && model.AllNews?.value?.text)) && (
              <StyledLink field={model.AllNews} target={targetLinkdsk(model?.AllNews, isDesktop)} />
            )}
            <div className={`case-news__popup ${secondaryCTAClassName} ${isEEActive ? 'generic-popupEE-width' : ''}`}>
              <Placeholder name={'jss-generic-popup'} rendering={props.rendering as ComponentRendering} />
            </div>
          </div>
        </div>
        <CustomCarousel
          settings={sliderSettings}
          id="case-news"
          fade
          slidesToScrollInitial={isDesktop ? 0.74 : 1}
          nItems={model.cards.length}
        >
          {generateCards}
        </CustomCarousel>
      </div>
      <div className="dots-wrapper"></div>
    </>
  );
};
export default withSitecoreContext()(News);
